/**
 * Date utility functions for Xtract AI
 * 
 * All timestamps from the API are UTC (stored as DATETIME2 with GETUTCDATE() in SQL Server)
 * but serialized WITHOUT a 'Z' suffix. Browsers treat offset-less ISO strings as local time,
 * which causes times to be 1 hour off during BST (UTC+1).
 * 
 * These utilities:
 * 1. Append 'Z' to API date strings so the browser knows they're UTC
 * 2. Always display in Europe/London timezone (handles GMT/BST automatically)
 */

const UK_TIMEZONE = 'Europe/London'

/**
 * Parse a UTC date string from the API into a proper Date object.
 * Appends 'Z' if the string doesn't already have a timezone indicator.
 */
export function parseUTC(dateString) {
  if (!dateString) return null
  const s = String(dateString).trim()
  // If it already has Z, +, or - offset info, parse as-is
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s)
  }
  // Append Z to tell the browser this is UTC
  return new Date(s + 'Z')
}

/**
 * Format date as "Apr 12, 2026" in UK timezone
 */
export function formatDate(dateString) {
  if (!dateString) return '-'
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return '-'
  return date.toLocaleDateString('en-GB', {
    month: 'short', day: 'numeric', year: 'numeric',
    timeZone: UK_TIMEZONE
  })
}

/**
 * Format time as "15:30" in UK timezone (24-hour for en-GB)
 */
export function formatTime(dateString) {
  if (!dateString) return ''
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return ''
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit',
    timeZone: UK_TIMEZONE
  })
}

/**
 * Format full datetime as "12 Apr 2026, 15:30" in UK timezone
 */
export function formatDateTime(dateString) {
  if (!dateString) return '-'
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return '-'
  return date.toLocaleString('en-GB', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: UK_TIMEZONE
  })
}

/**
 * Format as relative time: "2 hours ago", "3 days ago", etc.
 * Falls back to formatted date for older items.
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return ''
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return ''
  const now = new Date()
  const diffMs = now - date
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'Just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`
  const diffMonth = Math.floor(diffDay / 30)
  return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`
}

/**
 * "3 hours ago" within the last week, then "Apr 12, 2026" — recent work reads
 * faster as a distance, older work as a date.
 */
export function formatRecent(dateString) {
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return '-'
  const weekMs = 7 * 24 * 60 * 60 * 1000
  return Date.now() - date.getTime() < weekMs ? formatTimeAgo(dateString) : formatDate(dateString)
}

/**
 * Get a date group label: "Today", "Yesterday", "This Week", "This Month", or "January 2026"
 * Used for grouping activity items.
 */
export function getDateGroupLabel(dateString) {
  if (!dateString) return 'Unknown'
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return 'Unknown'

  // Get today/yesterday in UK timezone for comparison
  const ukNow = new Date(new Date().toLocaleString('en-US', { timeZone: UK_TIMEZONE }))
  const today = new Date(ukNow.getFullYear(), ukNow.getMonth(), ukNow.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const ukDate = new Date(date.toLocaleString('en-US', { timeZone: UK_TIMEZONE }))
  const itemDate = new Date(ukDate.getFullYear(), ukDate.getMonth(), ukDate.getDate())

  if (itemDate.getTime() === today.getTime()) return 'Today'
  if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday'

  const diffDays = Math.floor((today - itemDate) / (1000 * 60 * 60 * 24))
  if (diffDays < 7) return 'This Week'
  if (diffDays < 30) return 'This Month'
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: UK_TIMEZONE })
}

/**
 * Format for audit log timestamps: "Apr 12, 2026, 3:30 PM"
 */
export function formatAuditTimestamp(dateString) {
  if (!dateString) return ''
  const date = parseUTC(dateString)
  if (!date || isNaN(date)) return ''
  return date.toLocaleString('en-GB', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: UK_TIMEZONE
  })
}
